'use client';
import FeedItems from '@/components/complex/feed-items';
import styles from './styles.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HashTagDto } from '@/api/hashtag';
import { useAsyncEffect } from '@/hooks/use-async-effect';
import api from '@/api';
import { SearchResponse } from '@/api/search';
import Typography from '@/components/primitives/typography';
import Avatar from '@/components/primitives/avatar';
import Link from 'next/link';
import LNImage from '@/components/complex/ln-image';
import LikeButton from '@/screen/feed/view/components/like-button';
import { simplifyNumber } from '@/utils/number';

interface SearchResultViewProps {
  keyword: string;
}

const SearchResultView = ({ keyword }: SearchResultViewProps) => {
  const [result, setResult] = useState<SearchResponse>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasMoved(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      setIsDragging(false);
    }, 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // 스크롤 속도 조절
    if (Math.abs(walk) > 5) {
      setHasMoved(true);
    }
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    if (hasMoved) {
      e.preventDefault();
    }
  };

  useAsyncEffect(async () => {
    if (!keyword) {
      setResult(undefined);
      return;
    }
    try {
      setResult(await api.search.search(keyword));
    } catch (e) {
      console.error(e);
      setResult(undefined);
    }
  }, [keyword]);

  if (!result) return <></>;

  const noResult =
    (!result?.places || result?.places.length === 0) &&
    (!result?.accounts || result?.accounts.length === 0) &&
    (!result?.feeds || result?.feeds.length === 0) &&
    (!result?.hashtags || result?.hashtags.length === 0);
  const hasDiscoveryResults =
    (result?.hashtags?.length || 0) > 0 ||
    (result?.accounts?.length || 0) > 0 ||
    (result?.places?.length || 0) > 0;

  return (
    <div className={`${styles.container} ${styles.results} ${noResult ? styles.emptyResults : ''} ${!hasDiscoveryResults ? styles.feedOnly : ''}`}>
      {noResult && <Typography className={styles.noResult}
                               size='medium'
                               weight='regular'
                               align='center'
                               lineHeight={1.5}>
        검색 결과가 없어요!<br/>
        키워드를 바꿔서 다시 검색해보세요
      </Typography>}

      <div className={styles.discoveryRail}>
        {!!result?.hashtags && result?.hashtags.length !== 0 ? (
          <div className={styles.section}>
            <Typography
              className={styles.sectionTitle}
              weight="bold"
              size="medium"
            >
              해시태그
            </Typography>
            <div className={styles.hashtags}>
              {result.hashtags.map((hashtag) => (
                <HashtagCard key={hashtag.id} hashtag={hashtag} />
              ))}
            </div>
          </div>
        ) : <></>}

        {!!result?.accounts && result?.accounts.length !== 0 ? (
          <div className={styles.section}>
            <Typography
              className={styles.sectionTitle}
              weight="bold"
              size="medium"
            >
              계정
            </Typography>
            <div
              className={styles.accounts}
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              {result?.accounts?.map((account) => (
                <Link
                  key={account.name}
                  className={styles.account}
                  href={`/accounts/${account.name}`}
                  onClickCapture={handleLinkClick}
                >
                  <Avatar size={96} account={account} variant='w256'/>
                  <div className={styles.info}>
                    <Typography size="medium" weight="regular">
                      {account.nickname}
                    </Typography>
                    <Typography className={styles.name} size="small">
                      @{account.name}
                    </Typography>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}

        {!!result?.places && result?.places.length !== 0 ? (
          <div className={styles.section}>
            <Typography
              className={styles.sectionTitle}
              weight="bold"
              size="medium"
            >
              장소
            </Typography>
            <div className={styles.places}>
              {result?.places?.map((place) => (
                <Link
                  key={place.id}
                  className={styles.place}
                  href={`/places/${place.id}`}
                >
                  <div className={styles.info}>
                    {place.representativeImageUrl ? (
                      <LNImage
                        className={styles.image}
                        source={place.representativeImageUrl}
                        width={80}
                        height={80}
                        variant="w256"
                      />
                    ) : (
                      <div className={styles.emptyImage}>
                        <Typography size="extra-small" align="center">
                          연관 사진이<br />
                          없어요
                        </Typography>
                      </div>
                    )}
                    <div className={styles.text}>
                      <div className={styles.top}>
                        <Typography size="medium" weight="regular">
                          {place.name}
                        </Typography>
                        <div className={styles.address}>
                          <Typography size="small">
                            {place.streetAddress?.address}
                          </Typography>
                          <Typography size="small">
                            {place.streetAddress?.detail}
                          </Typography>
                        </div>
                      </div>
                      <div className={styles.statistics}>
                        <Typography size="extra-small">
                          좋아요 수: {simplifyNumber(place.likeCount || 0)}
                        </Typography>
                        <Typography size="extra-small">
                          연관 피드 수: {simplifyNumber(place.feedCount || 0)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <LikeButton
                    isLiked={place.isLike || false}
                    onChange={async (isLiked) => {
                      // 낙관적 업데이트: UI 상태 먼저 반영
                      setResult(prev => {
                        if (!prev || !prev.places) return prev;
                        return {
                          ...prev,
                          places: prev.places.map(p =>
                            p.id === place.id
                              ? {
                                  ...p,
                                  isLike: isLiked,
                                  likeCount: (p.likeCount || 0) + (isLiked ? 1 : -1)
                                }
                              : p
                          )
                        };
                      });

                      try {
                        if (isLiked) {
                          await api.placeLike.like(place.id);
                        } else {
                          await api.placeLike.unlike(place.id);
                        }
                      } catch (error) {
                        // 에러 발생 시 원래 상태로 롤백
                        setResult(prev => {
                          if (!prev || !prev.places) return prev;
                          return {
                            ...prev,
                            places: prev.places.map(p =>
                              p.id === place.id
                                ? {
                                    ...p,
                                    isLike: !isLiked,
                                    likeCount: (p.likeCount || 0) + (!isLiked ? 1 : -1)
                                  }
                                : p
                            )
                          };
                        });
                        console.error('좋아요 처리 중 오류 발생:', error);
                      }
                    }}
                  />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>

      <div className={`${styles.section} ${styles.feedSection}`}>
        <Typography
          className={styles.sectionTitle}
          weight="bold"
          size="medium"
        >
          피드
        </Typography>
        <FeedItems
          feeds={result?.feeds}
          keyword={keyword}
          infiniteScrollType='page'
          onEmptyResult={() => (
            <div className={styles.empty}>
              <Typography align="center" size="small">
                해당 키워드로 검색된 피드가 없어요
              </Typography>
            </div>
          )}
        />
      </div>
    </div>
  );
};

const HashtagCard = ({ hashtag }: { hashtag: HashTagDto }) => {
  const [imgError, setImgError] = useState(false);
  const handleError = useCallback(() => setImgError(true), []);
  const showImage = !!hashtag.representativeImageUrl && !imgError;

  return (
    <Link className={styles.hashtag} href={`/hashtags/${hashtag.name}`}>
      {showImage ? (
        <LNImage
          className={styles.hashtagImage}
          source={hashtag.representativeImageUrl!}
          width={140}
          height={140}
          variant="w256"
          onError={handleError}
        />
      ) : (
        <div className={styles.hashtagEmptyImage}>
          <span className={styles.hashtagHash}>#</span>
        </div>
      )}
      <div className={styles.hashtagBadge}>#</div>
      <div className={styles.hashtagOverlay}>
        <span className={styles.hashtagName}>#{hashtag.name}</span>
      </div>
    </Link>
  );
};

export default SearchResultView;
